import React from "react";
import { useRatingWorkflow } from "../../../../context/RatingWorkflowContext";
import type {
	AwardedProject,
	ReviewLevel,
} from "newui/features/MySuppliers/types/supplier-performance.types";

interface SelectProjectStepProps {
	awardedProjects: AwardedProject[];
	isLoading: boolean;
}

export const SelectProjectStep: React.FC<SelectProjectStepProps> = ({
	awardedProjects,
	isLoading,
}) => {
	const { state, dispatch } = useRatingWorkflow();

	const handleLevelChange = (level: ReviewLevel) => {
		dispatch({
			type: "SET_PROJECT",
			payload: {
				projectId: null,
				rfxId: null,
				rfxInviteId: null,
				reviewLevel: level,
			},
		});
	};

	const handleProjectSelect = (project: AwardedProject) => {
		if (state.reviewLevel === "project") {
			dispatch({
				type: "SET_PROJECT",
				payload: {
					projectId: project.project_id,
					rfxId: project.rfx_id,
					rfxInviteId: project.rfx_invite_id,
					reviewLevel: "project",
				},
			});
		} else {
			dispatch({
				type: "SET_PROJECT",
				payload: {
					projectId: null,
					rfxId: project.rfx_id,
					rfxInviteId: project.rfx_invite_id,
					reviewLevel: "rfx",
				},
			});
		}
	};

	return (
		<div data-testid="select-project-step">
			<h3 className="mb-4 text-lg font-semibold text-gray-900">
				Select Review Level
			</h3>
			<p className="mb-4 text-sm text-gray-600">
				Choose whether to rate at the project or RFx level.
			</p>

			<div className="mb-6 flex gap-4">
				<label
					className={`flex flex-1 cursor-pointer items-center gap-3 rounded-lg border p-4 ${
						state.reviewLevel === "project"
							? "border-blue-500 bg-blue-50"
							: "border-gray-200"
					}`}
					data-testid="review-level-project"
				>
					<input
						type="radio"
						name="reviewLevel"
						value="project"
						checked={state.reviewLevel === "project"}
						onChange={() => handleLevelChange("project")}
						className="h-4 w-4 text-blue-600"
					/>
					<div>
						<div className="text-sm font-medium text-gray-900">
							Project Level
						</div>
						<div className="text-xs text-gray-500">
							Rate performance on a specific project
						</div>
					</div>
				</label>

				<label
					className={`flex flex-1 cursor-pointer items-center gap-3 rounded-lg border p-4 ${
						state.reviewLevel === "rfx"
							? "border-blue-500 bg-blue-50"
							: "border-gray-200"
					}`}
					data-testid="review-level-rfx"
				>
					<input
						type="radio"
						name="reviewLevel"
						value="rfx"
						checked={state.reviewLevel === "rfx"}
						onChange={() => handleLevelChange("rfx")}
						className="h-4 w-4 text-blue-600"
					/>
					<div>
						<div className="text-sm font-medium text-gray-900">
							RFx Level
						</div>
						<div className="text-xs text-gray-500">
							Rate performance on an RFx engagement
						</div>
					</div>
				</label>
			</div>

			{isLoading && (
				<div className="py-8 text-center text-sm text-gray-500">
					Loading projects...
				</div>
			)}

			{!isLoading && awardedProjects.length === 0 && (
				<div className="py-8 text-center text-sm text-gray-500">
					No awarded projects found for this supplier.
				</div>
			)}

			{!isLoading && awardedProjects.length > 0 && (
				<div className="max-h-64 space-y-2 overflow-y-auto">
					{awardedProjects
						.filter((project) => {
							if (state.reviewLevel === "project") {
								return project.project_id !== null;
							}
							return true;
						})
						.filter((project, index, arr) => {
							if (state.reviewLevel === "project") {
								return arr.findIndex((p) => p.project_id === project.project_id) === index;
							}
							return true;
						})
						.map((project) => {
							const isSelected =
								state.reviewLevel === "project"
									? state.projectId === project.project_id
									: state.rfxId === project.rfx_id;

							const primaryLabel =
								state.reviewLevel === "project"
									? project.project_name
									: project.rfx_name;

							const secondaryLabel =
								state.reviewLevel === "project"
									? `RFx: ${project.rfx_name}`
									: project.project_name
										? `Project: ${project.project_name}`
										: null;

							return (
								<button
									key={project.rfx_invite_id}
									type="button"
									onClick={() => handleProjectSelect(project)}
									className={`flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors ${
										isSelected
											? "border-blue-500 bg-blue-50"
											: "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
									}`}
									data-testid={`project-option-${project.rfx_invite_id}`}
								>
									<div>
										<div className="text-sm font-medium text-gray-900">
											{primaryLabel}
										</div>
										{secondaryLabel && (
											<div className="text-xs text-gray-500">
												{secondaryLabel}
											</div>
										)}
									</div>
									{isSelected && (
										<span className="text-blue-600">✓</span>
									)}
								</button>
							);
						})}
				</div>
			)}
		</div>
	);
};
